import React, { useState } from "react";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { BadgeCheck, Check, AlertCircle, FileText, Download, Shield, Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import { useGetFieldForceCertificates } from "@features/wx/fieldForce/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { AddCertificationComponent } from "./addCertification";

interface CertificationsTabProps {
  technicianId: string;
}

interface CertificationData {
  id: number;
  name: string;
  document: string;
  verified: boolean;
}

export const CertificationsTab: React.FC<CertificationsTabProps> = ({ technicianId }) => {
  const { id } = useParams();
  const { data: getCertificates} = useGetFieldForceCertificates(id);
  
  // Dialog state management - following the ReadCycleLanding pattern
  const [showAddCertificationDialog, setShowAddCertificationDialog] = useState(false);

  const handleAddCertification = () => {
    setShowAddCertificationDialog(true);
  };

  const handleCloseAddCertificationModal = () => {
    setShowAddCertificationDialog(false);
  };

  const columns: TableColumn<CertificationData>[] = [
    {
      key: "name",
      header: "Certification Name",
      sortable: true,
    },
    {
      key: "document",
      header: "Document",
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-muted-foreground">{value}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Action",
      sortable: false,
      width: "w-32",
      render: (value: any, row: CertificationData) => (
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-1" />
          Download
        </Button>
      ),
    },
  ];
 
  return (
    <div className="">
      <div className="flex justify-between items-center mb-6">
        <h3 className="card-heading-text flex items-center">
          <BadgeCheck className="mr-2 h-5 w-5 text-blue-500" />
          Certifications
        </h3>
        
        <Button 
          onClick={handleAddCertification}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Certification
        </Button>
      </div>
      
      {getCertificates?.length > 0 ? (
        <div className="mt-2">
          <GenericTable
            data={getCertificates}
            columns={columns}
            rowKey="id"
            selectable={false}
            emptyMessage="No certifications found"
          />
        </div>
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium mb-2">No Certifications</h4>
          <p className="text-muted-foreground mb-4">This technician has no certifications yet.</p>
        </div>
      )}

      {/* Add Certification Modal */}
      <AddCertificationComponent
        isOpen={showAddCertificationDialog}
        onClose={handleCloseAddCertificationModal}
      />
    </div>
  );
};